import { RELIC_AWAKENED_FX, RELIC_FX } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { ANGLE_STEPS, iatan2, idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, PlayerId, Structure } from '../types.ts';
import { damageEnemy, enemiesNear, ev, healHero, targetable } from './combat.ts';
import { awake } from './gear.ts';
import { stunFor } from './towers.ts';

/*
 * The Unsleeping's relics (docs/ANTIHEROES.md, «Su reliquia»): each one a piece of his kit, for any class.
 * Every check starts from the hero's relic list (two at most), so a fight with nobody carrying them pays nothing.
 */

const standing = (h: Hero): boolean => h.downTicks === 0;

/** The standing hero behind a blow (none for a tower's or the keep's). */
const heroOf = (s: BattleState, pid: PlayerId): Hero | undefined => {
  if (!pid) return undefined;
  for (const h of s.heroes) if (h.pid === pid) return h.downTicks === 0 ? h : undefined;
  return undefined;
};

/** Ragna's horn: what the hero deals grows with the health it lost. */
export const ragnaBp = (h: Hero): number => {
  if (!h.relics.includes('ragnaHorn') || h.stats.maxHp <= 0) return 10000;
  const f = RELIC_FX.ragnaHorn;
  const lost = 10000 - imax(0, idiv(h.hp * 10000, h.stats.maxHp));
  const steps = idiv(lost, f.perBp);
  if (steps <= 0) return 10000;
  return 10000 + steps * (awake(h, 'ragnaHorn') ? RELIC_AWAKENED_FX.ragnaHorn.dmgBp : f.dmgBp);
};

/** A hero's blow before it lands: Ragna's horn. */
export const relicBlow = (s: BattleState, pid: PlayerId, dmg: number): number => {
  const h = heroOf(s, pid);
  return h ? mulBp(dmg, ragnaBp(h)) : dmg;
};

/** A hero's blow landed on `e`: Vela's string smokes it (the stronger mark stays). */
export const relicStruck = (s: BattleState, e: Enemy, pid: PlayerId): void => {
  if (e.hp <= 0) return;
  const h = heroOf(s, pid);
  if (!h || !h.relics.includes('velaString')) return;
  const bp = awake(h, 'velaString') ? RELIC_AWAKENED_FX.velaString.markBp : RELIC_FX.velaString.markBp;
  if ((e.markTicks ?? 0) > 0 && (e.markBp ?? 0) > bp) return;
  e.markBp = bp;
  e.markTicks = RELIC_FX.velaString.ticks;
};

/** Corvin's gate: a blow from a nightmare in front of the hero lands lighter. */
export const corvinGuard = (h: Hero, from: Enemy | null | undefined, amount: number): number => {
  if (!from || !h.relics.includes('corvinGate')) return amount;
  const f = RELIC_FX.corvinGate;
  const half = ANGLE_STEPS / 2;
  const d = ((((iatan2(from.y - h.y, from.x - h.x) - h.facing) % ANGLE_STEPS) + ANGLE_STEPS + half) % ANGLE_STEPS) - half;
  if (d > f.frontHalf || d < -f.frontHalf) return amount;
  return mulBp(amount, awake(h, 'corvinGate') ? RELIC_AWAKENED_FX.corvinGate.takenBp : f.takenBp);
};

/** Edda's shield: once a fight, a blow that would fell the hero leaves it untouchable and mended instead. True if it held. */
export const eddaHolds = (s: BattleState, h: Hero): boolean => {
  if (h.eddaUsed || !h.relics.includes('eddaShield')) return false;
  const f = awake(h, 'eddaShield') ? RELIC_AWAKENED_FX.eddaShield : RELIC_FX.eddaShield;
  h.eddaUsed = true;
  h.hp = imax(1, mulBp(h.stats.maxHp, f.healBp));
  if (h.invuln < f.invuln) h.invuln = f.invuln;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 900, kind: 'eddaShield' });
  return true;
};

/** Ansel's page: every few basic attacks, fire bursts around the target. */
export const anselPage = (s: BattleState, h: Hero, e: Enemy, dmg: number): void => {
  if (!h.relics.includes('anselPage')) return;
  const f = RELIC_FX.anselPage;
  const every = awake(h, 'anselPage') ? RELIC_AWAKENED_FX.anselPage.every : f.every;
  h.pages = (h.pages ?? 0) + 1;
  if (h.pages < every) return;
  h.pages = 0;
  const burst = mulBp(dmg, f.damageBp);
  ev(s, { t: 'boom', x: e.x, y: e.y, r: f.radius, kind: 'anselPage' });
  for (const o of enemiesNear(s, e.x, e.y, f.radius, (x) => targetable(s, x))) damageEnemy(s, o, burst, { pid: h.pid, type: 'fire', ignoresArmor: true });
};

/** Bruni's hammer: a structure near a standing carrier takes less (the best hammer near it counts). */
export const bruniBp = (s: BattleState, st: Structure): number => {
  let best = 10000;
  let c: { x: number; y: number } | null = null;
  for (const h of s.heroes) {
    if (!standing(h) || !h.relics.includes('bruniHammer')) continue;
    const bp = awake(h, 'bruniHammer') ? RELIC_AWAKENED_FX.bruniHammer.takenBp : RELIC_FX.bruniHammer.takenBp;
    if (bp >= best) continue;
    c ??= hexToWorld(st.q, st.r);
    if (withinRange(c.x, c.y, h.x, h.y, RELIC_FX.bruniHammer.range)) best = bp;
  }
  return best;
};

/** Lucan's censer: a nightmare dying near a carrier mends the heroes around it. */
export const lucanCenser = (s: BattleState, e: Enemy): void => {
  const f = RELIC_FX.lucanCenser;
  for (const h of s.heroes) {
    if (!standing(h) || !h.relics.includes('lucanCenser') || !withinRange(e.x, e.y, h.x, h.y, f.range)) continue;
    const bp = awake(h, 'lucanCenser') ? RELIC_AWAKENED_FX.lucanCenser.healBp : f.healBp;
    for (const o of s.heroes) if (standing(o) && withinRange(o.x, o.y, h.x, h.y, f.range)) healHero(s, o, imax(1, mulBp(o.stats.maxHp, bp)), h.pid);
  }
};

/**
 * Kaela's seed, once a tick per carrier: the nightmares around it count how long they have stood there unbroken
 * (`seedFrom`: the tick they came in; `seedAt`: the last tick seen), and the one that stayed long enough is pinned
 * once (`seedFrom` -1). Bosses, elites, guardians and the Unsleeping shrug it off, like a tower's stun.
 */
export const kaelaSeed = (s: BattleState, h: Hero): void => {
  if (!h.relics.includes('kaelaSeed')) return;
  const f = RELIC_FX.kaelaSeed;
  const stun = awake(h, 'kaelaSeed') ? RELIC_AWAKENED_FX.kaelaSeed.stun : f.stun;
  for (const e of enemiesNear(s, h.x, h.y, f.range)) {
    if (e.seedAt === s.tick || e.seedFrom === -1) continue;
    if (e.seedAt !== s.tick - 1 || e.seedFrom === undefined) e.seedFrom = s.tick;
    e.seedAt = s.tick;
    if (s.tick - e.seedFrom < f.after) continue;
    e.seedFrom = -1;
    const t = e.un ? 0 : stunFor(e, stun);
    if (t <= 0) continue;
    if (e.stun < t) e.stun = t;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'kaelaSeed' });
  }
};
